import React, { useState, useEffect } from 'react';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const HeroBanner = ({ featuredProducts = [] }) => {
  const [currentSlide, setCurrentSlide] = useState(0);

  // Auto-rotate slides every 5 seconds
  useEffect(() => {
    if (featuredProducts?.length > 1) {
      const timer = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % featuredProducts?.length);
      }, 5000);
      return () => clearInterval(timer);
    }
  }, [featuredProducts?.length]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const goToPrevious = () => {
    setCurrentSlide((prev) => 
      prev === 0 ? featuredProducts?.length - 1 : prev - 1
    );
  };

  const goToNext = () => {
    setCurrentSlide((prev) => (prev + 1) % featuredProducts?.length);
  };

  if (!featuredProducts || featuredProducts?.length === 0) {
    return (
      <div className="relative bg-gradient-to-r from-accent/10 to-accent/5 py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-4xl font-bold text-foreground mb-4">
              New Arrivals
            </h2>
            <p className="text-xl text-muted-foreground mb-8">
              Discover the latest fashion trends
            </p>
            <Button size="lg" iconName="ArrowRight" iconPosition="right">
              Shop Now
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const currentProduct = featuredProducts?.[currentSlide];

  return (
    <div className="relative bg-gradient-to-r from-accent/10 to-accent/5 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center py-20">
          {/* Content */}
          <div className="space-y-8">
            <div>
              <div className="inline-flex items-center space-x-2 bg-accent/20 text-accent px-3 py-1 rounded-full text-sm font-medium mb-4">
                <Icon name="Sparkles" size={14} />
                <span>New Arrival</span>
              </div>
              
              <h1 className="text-5xl font-bold text-foreground mb-4 leading-tight">
                {currentProduct?.name}
              </h1>
              
              <p className="text-xl text-muted-foreground mb-6">
                {currentProduct?.description}
              </p>

              <div className="flex items-center space-x-4 mb-8">
                <div className="flex items-center space-x-2">
                  {currentProduct?.originalPrice && (
                    <span className="text-lg text-muted-foreground line-through">
                      ${currentProduct?.originalPrice?.toFixed(2)}
                    </span>
                  )}
                  <span className="text-3xl font-bold text-foreground">
                    ${currentProduct?.price?.toFixed(2)}
                  </span>
                  {currentProduct?.discount && (
                    <span className="bg-error text-error-foreground px-2 py-1 rounded-full text-sm font-medium">
                      -{currentProduct?.discount}%
                    </span>
                  )}
                </div>

                {currentProduct?.rating && (
                  <div className="flex items-center space-x-1">
                    <Icon name="Star" size={16} className="text-warning fill-current" />
                    <span className="text-sm text-muted-foreground">
                      {currentProduct?.rating} ({currentProduct?.reviews} reviews)
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* <div className="flex flex-col sm:flex-row gap-4">
              <Button size="lg" iconName="ShoppingBag" iconPosition="left" className="flex-1 sm:flex-none">
                Add to Cart
              </Button>
              <Button variant="outline" size="lg" iconName="Heart" iconPosition="left">
                Add to Wishlist
              </Button>
            </div> */}

            {/* Product Features */}
            <div className="grid grid-cols-2 gap-4 pt-8 border-t border-border">
              <div className="flex items-center space-x-2">
                <Icon name="Truck" size={16} className="text-accent" />
                <span className="text-sm text-muted-foreground">Delivery all over Lebanon</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="RotateCcw" size={16} className="text-accent" />
                <span className="text-sm text-muted-foreground">No Exchange/Refund or Cancellation</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Shield" size={16} className="text-accent" />
                <span className="text-sm text-muted-foreground">Secure Payment</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Award" size={16} className="text-accent" />
                <span className="text-sm text-muted-foreground">Pre-Order Items</span>
              </div>
            </div>
          
          </div>

          {/* Product Image */}
          <div className="relative">
            <div className="relative aspect-square rounded-2xl overflow-hidden bg-card shadow-large">
              <Image
                src={currentProduct?.image}
                alt={currentProduct?.name}
                className="w-full h-full object-cover"
              />
              
              {/* Navigation Arrows */}
              {featuredProducts?.length > 1 && (
                <>
                  <Button
                    variant="secondary"
                    size="icon"
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-background/90 hover:bg-background shadow-lg"
                    onClick={goToPrevious}
                  >
                    <Icon name="ChevronLeft" size={20} />
                  </Button>
                  <Button
                    variant="secondary"
                    size="icon"
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-background/90 hover:bg-background shadow-lg"
                    onClick={goToNext}
                  >
                    <Icon name="ChevronRight" size={20} />
                  </Button>
                </>
              )}
            </div>

            {/* Slide Indicators */}
            {featuredProducts?.length > 1 && (
              <div className="flex justify-center space-x-2 mt-6">
                {featuredProducts?.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToSlide(index)}
                    className={`w-3 h-3 rounded-full transition-colors duration-200 ${
                      index === currentSlide ? 'bg-accent' : 'bg-muted hover:bg-muted-foreground'
                    }`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Background Decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-4 -right-4 w-72 h-72 bg-accent/5 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-8 -left-8 w-96 h-96 bg-accent/3 rounded-full blur-3xl"></div>
      </div>
    </div>
  );
};

export default HeroBanner;